import { Skeleton, Stack } from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/navigation/PageHeader';
import { useModalState } from '@/hooks/useModalState';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import {
  CONTINUE_ONBOARDING_FLOW_PARAM,
  CreateHouseholdFlow,
} from '@/modules/createHousehold/CreateHouseholdFlow/CreateHouseholdFlow';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { AI_SUGGESTION_MODAL_QUERY_PARAM } from '@/modules/notifications/constants';
import { ReviewAISuggestionsFromNotification } from '@/modules/notifications/ReviewAISuggestionsFromNotification/ReviewAISuggestionsFromNotification';
import { ClientDetailsPageActionItems } from '@/pages/ClientDetailsPage/ClientDetailsPageActionItems';

import { ClientOverviewPageContent } from './ClientOverviewPageContent';
import { EnhancedHouseholdOverview } from './EnhancedHouseholdOverview';
import { useClientDetailsOverviewPageQuery } from './hooks/useClientDetailsOverviewPageQuery';

export function ClientOverviewPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const householdId = useRequiredParam('householdId');
  const enhancedSetup = useFeatureFlag('enhanced_household_onboarding_flow');

  // handle onboarding flow
  const continueOnboardingStep = searchParams.get(
    CONTINUE_ONBOARDING_FLOW_PARAM
  );

  // handle AI suggestion modal
  const shouldShowAISuggestionModal = searchParams.has(
    AI_SUGGESTION_MODAL_QUERY_PARAM
  );
  const [
    { isModalOpen: isAISuggestionModalOpen },
    { closeModal: closeAISuggestionModal },
  ] = useModalState(shouldShowAISuggestionModal);

  const handleCloseAISuggestionModal = () => {
    // Clear the query parameter from URL
    setSearchParams(
      (params) => {
        params.delete(AI_SUGGESTION_MODAL_QUERY_PARAM);
        return params;
      },
      { replace: true }
    );

    closeAISuggestionModal();
  };
  const {
    household,
    hasEntities,
    hasNoDocuments,
    assetsSummary,
    loading,
    error,
    refetch,
  } = useClientDetailsOverviewPageQuery({
    householdId,
  });

  const header = (
    <PageHeader
      heading={household ? household.displayName : <Skeleton width={50} />}
      action={
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1}
        >
          <ClientDetailsPageActionItems
            householdId={householdId}
            key={location.pathname}
          />
        </Stack>
      }
    />
  );
  return (
    <>
      {enhancedSetup && (
        <EnhancedHouseholdOverview
          header={header}
          key={householdId}
          householdId={householdId}
          household={household}
          hasEntities={hasEntities}
          hasNoDocuments={hasNoDocuments}
          assetsSummary={assetsSummary}
          overviewLoading={loading}
          overviewFailed={!!error}
          refreshOverview={refetch}
        />
      )}
      {!enhancedSetup && (
        <Stack>
          {header}
          <Stack p={2} flex={1}>
            <ClientOverviewPageContent
              householdId={householdId}
              household={household}
              hasEntities={hasEntities}
              assetsSummary={assetsSummary}
            />
          </Stack>
        </Stack>
      )}
      <CreateHouseholdFlow
        isOpen={!!continueOnboardingStep}
        onClose={() => {
          setSearchParams(
            (params) => {
              params.delete(CONTINUE_ONBOARDING_FLOW_PARAM);
              return params;
            },
            { replace: true }
          );
        }}
        continueFrom={continueOnboardingStep}
        continueFromHouseholdId={householdId}
      />
      <ReviewAISuggestionsFromNotification
        isOpen={isAISuggestionModalOpen}
        onClose={handleCloseAISuggestionModal}
        householdId={householdId}
      />
    </>
  );
}
